import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { useData } from '../../context/DataContext';
import { User, Save, MapPin, Mail, Building2, Briefcase, Phone } from 'lucide-react';
import ChangeMobileForm from './ChangeMobileForm';

/**
 * Main profile form: name, email, city, company, type, address, GST.
 * Embeds <ChangeMobileForm/> for the mobile-update OTP flow.
 */
const CustomerProfileForm = ({ user, authClient, onUpdated }) => {
  const { toast } = useToast();
  const { cities = [] } = useData() || {};
  const [form, setForm] = useState({
    name: '', email: '', city: '', company: '', customer_type: 'Individual', address: '', gst: '',
  });
  const [saving, setSaving] = useState(false);
  const [showChangeMobile, setShowChangeMobile] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '', email: user.email || '', city: user.city || '',
        company: user.company || '', customer_type: user.customer_type || 'Individual',
        address: user.address || '', gst: user.gst || '',
      });
    }
  }, [user]);

  const onSave = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast({ title: 'Name is required', variant: 'destructive' });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast({ title: 'Invalid email format', variant: 'destructive' });
    setSaving(true);
    try {
      const r = await authClient.put('/auth/me', form);
      localStorage.setItem('tgmart_user', JSON.stringify(r.data));
      window.dispatchEvent(new Event('tgmart-session-change'));
      toast({ title: 'Profile updated' });
      onUpdated && onUpdated(r.data);
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-6">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-1">
        <User className="w-5 h-5 text-[#ee5e23]" /> Profile information
      </h3>
      <p className="text-xs text-gray-500 mb-4">Your mobile number is your account ID. You can change it after verifying the new number with an OTP.</p>

      <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2 bg-gray-50 border border-gray-200 rounded p-3" data-testid="mobile-section">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <Label className="text-xs text-gray-700"><Phone className="w-3 h-3 inline mr-1" /> Mobile number</Label>
              <div className="text-sm font-mono text-gray-900 mt-0.5" data-testid="profile-mobile-display">+91 {user?.mobile || '—'}</div>
            </div>
            {!showChangeMobile && (
              <Button type="button" size="sm" variant="outline" onClick={() => setShowChangeMobile(true)} data-testid="change-mobile-btn">
                <Phone className="w-3.5 h-3.5 mr-1" /> Change mobile
              </Button>
            )}
          </div>
          {showChangeMobile && (
            <ChangeMobileForm user={user} authClient={authClient} onUpdated={onUpdated} onDone={() => setShowChangeMobile(false)} />
          )}
        </div>
        <div>
          <Label className="text-xs text-gray-700">Full name <span className="text-red-500">*</span></Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mt-1" data-testid="profile-name" />
        </div>
        <div>
          <Label className="text-xs text-gray-700"><Mail className="w-3 h-3 inline mr-1" /> Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1" data-testid="profile-email" />
        </div>
        <div>
          <Label className="text-xs text-gray-700"><Building2 className="w-3 h-3 inline mr-1" /> Company / business</Label>
          <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name (optional)" className="mt-1" data-testid="profile-company" />
        </div>
        <div>
          <Label className="text-xs text-gray-700"><Briefcase className="w-3 h-3 inline mr-1" /> Buyer type</Label>
          <select value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} className="w-full h-10 mt-1 border border-gray-300 rounded px-2 text-sm focus:outline-none focus:border-[#ee5e23]" data-testid="profile-customer-type">
            <option value="Individual">Individual buyer</option>
            <option value="Business">Business / Wholesaler</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-gray-700"><MapPin className="w-3 h-3 inline mr-1" /> City</Label>
          <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full h-10 mt-1 border border-gray-300 rounded px-2 text-sm focus:outline-none focus:border-[#ee5e23]" data-testid="profile-city">
            <option value="">Select city</option>
            {(cities.length ? cities.map((c) => c.name) : ['Hyderabad', 'Warangal', 'Nizamabad']).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs text-gray-700">Address (optional)</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, area, landmark, PIN code" className="mt-1" data-testid="profile-address" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs text-gray-700">GST number (optional, for business buyers)</Label>
          <Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })} placeholder="22ABCDE1234F1Z5" maxLength={15} className="mt-1 font-mono" data-testid="profile-gst" />
        </div>
        <div className="md:col-span-2 flex justify-end pt-2 border-t border-gray-100">
          <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15]" data-testid="profile-save-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save profile'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerProfileForm;
